import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./AddBlog.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

export default function BlogEditor({ initialBlog = null, onSaved }) {
  const token = localStorage.getItem("token");

  const [title, setTitle] = useState(initialBlog?.title || "");
  const [content, setContent] = useState(initialBlog?.content || "");
  const [coverImage, setCoverImage] = useState(null); // newly selected File
  const [coverImageUrl, setCoverImageUrl] = useState(initialBlog?.image || ""); // existing URL (when editing)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const quillRef = useRef();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(initialBlog?.category?.id || "");

  // sync when parent provides/changes initialBlog (important when editing after async fetch)
  useEffect(() => {
    setTitle(initialBlog?.title || "");
    setContent(initialBlog?.content || "");
    setCoverImage(null);
    setCoverImageUrl(initialBlog?.image || "");
    setSelectedCategory(initialBlog?.category?.id || "");
  }, [initialBlog]);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await fetch(`${API_BASE}/api/admin/categories`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    }
    fetchCategories();
  }, []);


  // helper to safely parse JSON or return raw text for debugging
  const parseResponseSafely = async (res) => {
    const text = await res.text();
    try {
      return { ok: res.ok, data: text ? JSON.parse(text) : {}, raw: text };
    } catch {
      return { ok: res.ok, data: null, raw: text };
    }
  };

  const getEditorHtml = () => {
    if (quillRef.current && quillRef.current.getEditor) {
      try {
        const editor = quillRef.current.getEditor();
        return editor.root.innerHTML;
      } catch {
        // fallback
      }
    }
    return content;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const isEdit = Boolean(initialBlog && initialBlog.id);

    try {
      // Basic client-side validation
      if (!title.trim()) throw new Error("لطفاً عنوان را وارد کنید.");
      const htmlContent = getEditorHtml();
      if (!htmlContent || String(htmlContent).trim() === "" || String(htmlContent).trim() === "<p><br></p>") {
        throw new Error("لطفاً محتوای مقاله را وارد کنید.");
      }

      // CREATE (new) requires a cover image (backend validation expects 'image')
      if (!isEdit && !coverImage) {
        throw new Error("لطفاً تصویر کاور را انتخاب کنید.");
      }

      // --- If editing and a new file was selected, send multipart POST + _method=PUT ---
      if (isEdit && coverImage) {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("content", htmlContent);
        // backend expects 'cover_image' in update validation
        fd.append("cover_image", coverImage);
        // method override so Laravel treats this as PUT and parses files
        fd.append("_method", "PUT");

        const res = await fetch(`${API_BASE}/api/blogs/${initialBlog.id}`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            Accept: "application/json",
            // DO NOT set Content-Type
          },
          body: fd,
        });

        const { ok, data, raw } = await parseResponseSafely(res);
        if (!ok) {
          // if server returned non-json raw HTML, include a short snippet
          const snippet = raw?.slice(0, 1000);
          throw new Error((data && data.message) || `خطا در بروزرسانی مقاله. پاسخ سرور: ${snippet}`);
        }

        toast.success("مقاله با موفقیت بروزرسانی شد.");
        if (onSaved) onSaved(data);
        return;
      }

      // --- If editing and NO new file: send JSON PUT (backend accepts JSON for title/content) ---
      if (isEdit && !coverImage) {
        const payload = { title, content: htmlContent, category_id: selectedCategory, };

        const res = await fetch(`${API_BASE}/api/blogs/${initialBlog.id}`, {
          method: "PUT",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        const { ok, data, raw } = await parseResponseSafely(res);
        if (!ok) {
          const snippet = raw?.slice(0, 1000);
          throw new Error((data && data.message) || `خطا در بروزرسانی مقاله. پاسخ سرور: ${snippet}`);
        }

        toast.success("مقاله با موفقیت بروزرسانی شد.");
        if (onSaved) onSaved(data);
        return;
      }

      // --- CREATE NEW BLOG (multipart because image required) ---
      if (!isEdit) {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("content", htmlContent);
        fd.append("image", coverImage); // store() in store() expects 'image'
        fd.append("category_id", selectedCategory);
        const res = await fetch(`${API_BASE}/api/blogs`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            Accept: "application/json",
          },
          body: fd,
        });

        const { ok, data, raw } = await parseResponseSafely(res);
        if (!ok) {
          const snippet = raw?.slice(0, 1000);
          throw new Error((data && data.message) || `خطا در ایجاد مقاله. پاسخ سرور: ${snippet}`);
        }

        toast.success("مقاله با موفقیت ایجاد شد.");
        if (onSaved) onSaved(data);
        return;
      }
    } catch (err) {
      console.error("handleSave error:", err);
      setError(err.message || "خطا در ذخیره مقاله");
      toast.error(err.message || "خطا در ذخیره مقاله");
    } finally {
      setSaving(false);
    }
  };

  const imageHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("image", file);

      try {
        const res = await fetch(`${API_BASE}/api/blogs/upload-image`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            Accept: "application/json",
          },
          body: formData,
        });

        const text = await res.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          data = null;
        }

        if (res.ok && data?.url) {
          const editor = quillRef.current.getEditor();
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, "image", data.url);
        } else {
          console.error("Upload error response:", text);
          toast.error("آپلود تصویر ناموفق بود.", { position: "top-right" });
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error("خطا در آپلود تصویر", { position: "top-right" });
      }
    };
  };
  const videoHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/*";
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("video", file);

      try {
        const res = await fetch(`${API_BASE}/api/blogs/upload-video`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            Accept: "application/json",
          },
          body: formData,
        });

        const data = await res.json();
        if (res.ok && data?.url) {
          const editor = quillRef.current.getEditor();
          const range = editor.getSelection(true);
          // Insert HTML5 <video> instead of iframe
          editor.insertEmbed(range.index, "video", data.url);
        } else {
          console.error("Upload failed:", data);
          toast.error("آپلود ویدیو ناموفق بود.");
        }
      } catch (err) {
        console.error("Error uploading video:", err);
        toast.error("خطا در آپلود ویدیو.");
      }
    };
  };


  const modules = {
    toolbar: {
      container: [
        [{ font: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ header: [1, 2, 3, 4, 5, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image", "video", "blockquote", "code-block"],
        [{ color: [] }, { background: [] }],
        [{ direction: "rtl" }],
        ["clean"],
      ],
      handlers: { 
        image: imageHandler, 
        video: videoHandler
      },
       clipboard: {
        matchers: [
          ["p", (node, delta) => {
            // Convert pasted bullets (•) into proper list items
            if (node.innerText.startsWith("•")) {
              const text = node.innerText.replace(/^•\s*/, "");
              return { ops: [{ insert: text, attributes: { list: "bullet" } }] };
            }
            return delta;
          }],
        ],
      }
    },
  };

  return (
    <>
      <form onSubmit={handleSave} className="blog-editor-form">
        <label>عنوان</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label>دسته‌بندی</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          required
        >
          <option value="">-- انتخاب دسته‌بندی --</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
        <label>تصویر کاور {initialBlog ? "(در صورت انتخاب تصویر جدید جایگزین می‌شود)" : ""}</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            setCoverImage(e.target.files[0] || null);
          }}
        />
        {coverImageUrl && !coverImage && (
          <div className="cover-preview">
            <img src={coverImageUrl} alt="Cover" />
          </div>
        )}

        <label>محتوا</label>
        <ReactQuill ref={quillRef} value={content} onChange={setContent} modules={modules} theme="snow" />

        {error && <div className="error">{error}</div>}

        <button type="submit" disabled={saving}>
          {saving ? "در حال ذخیره..." : initialBlog ? "به‌روزرسانی" : "ایجاد بلاگ"}
        </button>
      </form>

      <ToastContainer />
    </>
  );
}
